import {
  Box,
  type BoxProps,
  Button,
  type ButtonProps,
  Flex,
  Text,
  Title,
  Tooltip,
} from '@mantine/core'
import { type ReactNode, type PropsWithChildren } from 'react'

import { useAnalytics } from '@flecto/analytics'
import { useTranslation } from '@flecto/translations'

import { useOnboardingNavigation } from '../../hooks/useOnboardingNavigation'

type StepWrapperProps = PropsWithChildren<BoxProps> & {
  title?: string
  stepNumber: number
  description?: string
  extraAction?: ReactNode
  withActions?: boolean
  withStepsCount?: boolean
  buttonNextProps?: ButtonProps & { tooltip?: string }
  nextStepDisabled?: boolean
  onPrevStep?: () => void
  onNextStep?: () => void
}

export function StepWrapper({
  title,
  stepNumber,
  description,
  children,
  onPrevStep,
  onNextStep,
  extraAction,
  buttonNextProps,
  withActions = true,
  withStepsCount = true,
  nextStepDisabled,
  ...props
}: StepWrapperProps) {
  const track = useAnalytics('segment')
  const { currentStep, totalSteps, prevStep } = useOnboardingNavigation()
  const { t } = useTranslation('saas', { keyPrefix: 'onboarding' })

  if (currentStep !== stepNumber) {
    return null
  }

  const trackNavigation = (to: number) => {
    track('ONBOARDING_STEP_NAVIGATION', {
      step_from: currentStep,
      step_to: to,
    })
  }

  const handlePrevStep = () => {
    trackNavigation(currentStep - 1)
    onPrevStep?.()
    prevStep()
  }

  const handleNextStep = () => {
    trackNavigation(currentStep + 1)
    onNextStep?.()
  }

  const nextStepLabel =
    currentStep >= totalSteps ? t('actions.finish') : t('actions.next_step')

  return (
    <Box {...props}>
      {withStepsCount && (
        <Text
          c='dimmed'
          size='sm'
        >{`${t('step')} ${currentStep} ${t('of')} ${totalSteps}`}</Text>
      )}
      <Title order={4}>{title}</Title>
      <Text c='dimmed' mb='lg' mt='sm'>
        {description}
      </Text>
      {children}
      {withActions && (
        <Flex align='center' justify='space-between' gap='md' mt='lg'>
          <Box>{extraAction}</Box>
          <Flex align='center' gap='md'>
            <Button
              variant='default'
              onClick={() => handlePrevStep()}
              disabled={currentStep === 1}
            >
              {t('actions.prev_step')}
            </Button>
            <Tooltip
              position='top'
              label={buttonNextProps?.tooltip}
              disabled={!buttonNextProps?.tooltip || !nextStepDisabled}
            >
              <Button
                variant='filled'
                onClick={handleNextStep}
                {...buttonNextProps}
                disabled={nextStepDisabled || buttonNextProps?.disabled}
              >
                {nextStepLabel}
              </Button>
            </Tooltip>
          </Flex>
        </Flex>
      )}
    </Box>
  )
}
